import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';

import { UserService } from '../../services/core.user.service';
import { RefusedError } from '../../services/core.refused-error';
import { TourService } from '../tour/tour.service';
import { SiteConfigService } from '../../services/core.site-config.service';
import { WikiService } from '../../../widgets/wiki/services/wiki.service';
import { WikiHelperService } from '../../../widgets/wiki/services/wiki.helper';
import { StateService } from '@uirouter/angular';

@Injectable()
export class AdminResolvesService {

    constructor(
        private userService: UserService,
        private siteConfigService: SiteConfigService,
        private tourService: TourService,
        private wikiService: WikiService,
        private wikiHelper: WikiHelperService,
        private stateService: StateService,
    ) {
    }

    /**
     * Site-admin guard for /admin/*.
     *
     * The two refusals are different: an anonymous visitor needs to log in (and to be
     * brought back here afterwards), while a signed-in non-admin is not going to become
     * an admin by logging in again — they get the denial target instead. Rejecting with
     * a status lets the ui-router error hook do both. See docs/PERMISSION-REDIRECT.md.
     */
    security() {
        if (this.userService.session.isSiteAdmin()) {
            return Promise.resolve();
        }

        if (! this.userService.session.hasUser()) {
            return Promise.reject(RefusedError.unauthorized('Site admin area requires a session'));
        }

        return Promise.reject(RefusedError.forbidden('User is not a site admin'));
    }

    userTour(transition: any) {
        // When a state has children, you have to avoid
        // inheriting the tour setting.
        if (transition && transition.to().name === 'home.admin.users') {
            return this.tourService.setTour('siteAdminUsersCtrl');
        }
    }

    configTour() {
        return this.tourService.setTour('adminConfigSettingsCtrl');
    }

    homepageTour() {
        return this.tourService.setTour('siteAdminHomepageCtrl');
    }

    homepageSettings() {
        const siteConfig = this.siteConfigService.getSiteConfig();
        return {
            useCustomSiteSplash: _.get(window, 'useCustomSiteSplash', false) === 'Y' ? 'Y' : 'N',
            customLogoUrl: _.get(siteConfig, 'customLogoUrl', ''),
            customCssUrl: _.get(siteConfig, 'customCssUrl', ''),
            customFooterUrl: _.get(siteConfig, 'customFooterUrl', ''),
            showApiDocs: _.get(siteConfig, 'showApiDocs', 0),
            siteName: _.get(siteConfig, 'siteName', '')
        };
    }

    troveTour() {
        return this.tourService.setTour('siteAdminTroveCtrl');
    }

    projectsTour() {
        return this.tourService.setTour('siteAdminProjectsCtrl');
    }

    snapshotsTour() {
        return this.tourService.setTour('siteAdminSnapshotsCtrl');
    }

    authTour() {
        return this.tourService.setTour('siteAdminAuthCtrl');
    }

    notificationTour() {
        return this.tourService.setTour('siteAdminNotifCtrl');
    }

    async homepageWikiHtml(stateParams?: any): Promise<string> {
        const id = _.get(stateParams, 'pagename', 'FrontPage');

        if (id === '') return '';

        const urlFn = (pagename: string) => {
            return this.stateService.href('.', { pagename: pagename });
        };

        const params = {
            section: 'admin',
            refId: 0,
            editable: false,
        };

        try {
            const data = await this.wikiService.page(id, params);
            const wikiContent = _.get(data, 'data', '');
            const wikiHtml = this.wikiHelper.convertWikiWords(wikiContent, urlFn);
            return wikiHtml;
        } catch {
            return '';
        }
    }

    async homepageWikiPage(stateParams?: any): Promise<any> {
        const pageName = stateParams?.pageName;

        try {
            const data = await this.wikiService.info(encodeURIComponent(pageName), {
                section: 'admin',
                refId: 0,
                rel: 'monitor,options,version,content'
            });
            return data;
        } catch {
            const wikiPage = { pagename: pageName, rel: { content: { data: null } } };
            _.set(wikiPage, 'rel.content.data', this.wikiHelper.getContents(wikiPage));
            return wikiPage;
        }
    }

    homepagePageName(stateParams?: any) {
        return stateParams?.pageName;
    }

}
